import {
  SelectGroup
} from "./chunk-CCTQOKNE.mjs";
import {
  SelectItem
} from "./chunk-NOPPTQHN.mjs";
import {
  SelectSeparator
} from "./chunk-TWGLAEV3.mjs";
import {
  ChevronDown,
  ChevronUp
} from "./chunk-5VDSC7QK.mjs";

// src/select/src/select.tsx
import { forwardRef, useId, useState } from "react";
import { cx } from "class-variance-authority";
import * as RadixSelectPrimitive from "@radix-ui/react-select";
import { HintsAndErrorMessage, Label } from "@thisisbud/gds-shared-utilities";
import { jsx, jsxs } from "react/jsx-runtime";
var scrollClasses = "flex h-6 cursor-default items-center justify-center rounded-md bg-surface-light text-surface-body [&_svg]:size-5";
var SelectRoot = forwardRef(
  ({
    children,
    label,
    hint,
    errorMessage,
    placeholder = "",
    disabled = false,
    hideLabel = false,
    helperText,
    ...rest
  }, forwardedRef) => {
    const labelId = useId();
    const hintId = useId();
    const errorId = useId();
    const helperTextId = useId();
    const hasHint = Boolean(hint);
    const hasError = Boolean(errorMessage);
    const hasHelperText = Boolean(helperText);
    const [portalContainer, setPortalContainer] = useState(null);
    return /* @__PURE__ */ jsxs("div", { children: [
      /* @__PURE__ */ jsx(
        Label,
        {
          disabled,
          hideLabel,
          labelId,
          text: label
        }
      ),
      /* @__PURE__ */ jsx(
        HintsAndErrorMessage,
        {
          disabled,
          errorId,
          errorMessage,
          hasError,
          hasHint,
          hint,
          hintId
        }
      ),
      /* @__PURE__ */ jsxs(RadixSelectPrimitive.Root, { ...rest, children: [
        /* @__PURE__ */ jsx("div", { className: "flex", children: /* @__PURE__ */ jsxs(
          RadixSelectPrimitive.Trigger,
          {
            className: cx(
              `inline-flex h-10 grow appearance-none items-center justify-between gap-2 overflow-hidden
                 rounded bg-surface-light px-3 text-base text-surface-dark outline-none ring-1 ring-surface-border
            focus:outline-none focus:ring-2 focus:!ring-surface-dark data-[disabled]:bg-surface-light
            data-[disabled]:text-interactive-disabled data-[placeholder]:text-surface-body
            data-[disabled]:!ring-interactive-disabled
            `,
              {
                "!ring-state-negative-graphics": hasError
              }
            ),
            disabled,
            id: labelId,
            ref: forwardedRef,
            children: [
              /* @__PURE__ */ jsx(
                "div",
                {
                  className: cx("truncate", {
                    "!text-interactive-disabled": disabled
                  }),
                  children: /* @__PURE__ */ jsx(RadixSelectPrimitive.Value, { placeholder })
                }
              ),
              /* @__PURE__ */ jsx(
                RadixSelectPrimitive.Icon,
                {
                  className: cx("", {
                    "!text-interactive-disabled": disabled
                  }),
                  children: /* @__PURE__ */ jsx(ChevronDown, {})
                }
              )
            ]
          }
        ) }),
        /* @__PURE__ */ jsx("div", { ref: setPortalContainer }),
        /* @__PURE__ */ jsx(RadixSelectPrimitive.Portal, { container: portalContainer, children: /* @__PURE__ */ jsxs(
          RadixSelectPrimitive.Content,
          {
            className: "z-10 max-h-[var(--radix-select-content-available-height)] min-w-[var(--radix-select-trigger-width)] max-w-[var(--radix-select-content-available-width)] rounded border border-surface-strong bg-surface-light shadow-[0px_2px_4px_0px_rgba(119,_144,_181,_0.25)]",
            position: "popper",
            children: [
              /* @__PURE__ */ jsx(RadixSelectPrimitive.ScrollUpButton, { className: scrollClasses, children: /* @__PURE__ */ jsx(ChevronUp, {}) }),
              /* @__PURE__ */ jsx(RadixSelectPrimitive.Viewport, { className: "mx-1 my-2", children }),
              /* @__PURE__ */ jsx(RadixSelectPrimitive.ScrollDownButton, { className: scrollClasses, children: /* @__PURE__ */ jsx(ChevronDown, {}) })
            ]
          }
        ) })
      ] }),
      hasHelperText ? /* @__PURE__ */ jsx(
        "p",
        {
          className: cx("mt-1 text-xs text-surface-body", {
            "!text-interactive-disabled": disabled
          }),
          id: helperTextId,
          children: helperText
        }
      ) : null
    ] });
  }
);
var Select = {
  ...SelectRoot,
  Group: SelectGroup,
  Item: SelectItem,
  Separator: SelectSeparator
};

export {
  Select
};
